/**
 * Editor-level renderer back-end preference.
 *
 * # Why a separate store
 *
 * The plan section §6 PR-A speaks about an `editor.renderer` "sysvar".
 * The existing `sysvarStore` (`apps/web/src/app/dynamicInput/sysvarStore.ts`)
 * is strictly typed for AutoCAD-parity integer-valued system variables
 * (DYNMODE, OSMODE, ORTHOMODE, ...). Mixing a string-valued option
 * into that schema would dilute its AutoCAD parity — none of the
 * AutoCAD `SETVAR` commands operate on `"webgl"` / `"svg"` literals.
 *
 * Instead this module mirrors the pattern used by
 * `previewPreferences.ts`: a tiny zustand store with localStorage
 * persistence. Integration points (App.tsx) read `useRendererPref()`
 * directly, side by side with `useSysvarStore`.
 *
 * # URL override (Q2 user decision — PR-K one-way migration)
 *
 * `?renderer=svg|webgl` URL fallback was REMOVED at PR-K. Q2 user
 * decision (2026-05-06): one-way flip with no SVG fallback path. The
 * `applyUrlRendererOverride()` export below is now a deprecated no-op
 * — kept only as an export-stable shim so callers compile through the
 * PR. Rollback strategy is `git revert` of the PR-K commit, not a
 * runtime override.
 *
 * # Default
 *
 * PR-K (2026-05-05): default flipped from `"svg"` to `"webgl"`. The
 * legacy SVG renderer (`PrimitiveAtlasStage`) was deleted in the same
 * PR — the field is retained as a single-value enum for forward-
 * compatibility (3D mode in PR-L will introduce additional renderer
 * variants).
 */

import { create } from "zustand";

export type RendererBackendChoice = "svg" | "webgl";

export interface RendererPreference {
  /** Currently selected renderer back-end. */
  renderer: RendererBackendChoice;
}

export const DEFAULT_RENDERER_PREFERENCE: RendererPreference = {
  renderer: "webgl",
};

const LOCAL_STORAGE_KEY = "edit-dwg.renderer";

function isValidChoice(value: unknown): value is RendererBackendChoice {
  return value === "svg" || value === "webgl";
}

export function readPersistedRendererPreference(): RendererPreference {
  if (typeof window === "undefined") {
    return { ...DEFAULT_RENDERER_PREFERENCE };
  }
  try {
    const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_RENDERER_PREFERENCE };
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") {
      return { ...DEFAULT_RENDERER_PREFERENCE };
    }
    const candidate = parsed as Partial<RendererPreference>;
    return {
      renderer: isValidChoice(candidate.renderer)
        ? candidate.renderer
        : DEFAULT_RENDERER_PREFERENCE.renderer,
    };
  } catch {
    return { ...DEFAULT_RENDERER_PREFERENCE };
  }
}

export function persistRendererPreference(prefs: RendererPreference): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable (private mode / quota) — ignore.
  }
}

interface RendererPreferenceStore extends RendererPreference {
  /** Persisting setter. */
  setRenderer: (next: RendererBackendChoice) => void;
  /** In-memory setter (URL override / session-only). */
  setRendererSession: (next: RendererBackendChoice) => void;
  reset: () => void;
}

const useRendererPreferenceStore = create<RendererPreferenceStore>(
  (set, get) => ({
    ...readPersistedRendererPreference(),
    setRenderer: (next) => {
      const merged: RendererPreference = { renderer: next };
      persistRendererPreference(merged);
      set({ renderer: next });
    },
    setRendererSession: (next) => {
      // No persist call — used by URL override so reload without the
      // query string returns to whatever the user actually saved.
      set({ renderer: next });
    },
    reset: () => {
      persistRendererPreference(DEFAULT_RENDERER_PREFERENCE);
      set({ ...DEFAULT_RENDERER_PREFERENCE });
    },
  }),
);

/**
 * React hook — returns either the whole store (no argument) or a
 * selected slice (selector argument). Mirrors the zustand idiom used
 * by `usePreviewPreferences` so the two stores look the same to
 * consumers.
 */
export function useRendererPreference(): RendererPreferenceStore;
export function useRendererPreference<T>(
  selector: (store: RendererPreferenceStore) => T,
): T;
export function useRendererPreference<T>(
  selector?: (store: RendererPreferenceStore) => T,
): RendererPreferenceStore | T {
  if (selector) {
    return useRendererPreferenceStore(selector);
  }
  return useRendererPreferenceStore();
}

/**
 * Pure getter for non-React callers. Mirrors the
 * `getPreviewPreferences()` pattern.
 */
export function getRendererPreference(): RendererPreference {
  const state = useRendererPreferenceStore.getState();
  return { renderer: state.renderer };
}

/**
 * @deprecated PR-K (2026-05-05): URL renderer override REMOVED per Q2
 * user decision (one-way migration to WebGL). The function body is
 * intentionally inert — it returns `null` unconditionally so the
 * persisted / default `"webgl"` value always wins. The export is
 * retained only as a stable shim so App.tsx and existing tests keep
 * compiling through the PR; remove after one release.
 *
 * Rollback to legacy SVG renderer = `git revert` of the PR-K commit,
 * NOT a runtime URL flag.
 */
export function applyUrlRendererOverride(): RendererBackendChoice | null {
  return null;
}
